// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	clampMediaTime,
	detachMediaElementSource,
} from '@app/features/voice/components/media_player/utils/MediaSeekUtils';
import styles from '@app/features/voice/components/media_player/VideoPlayer.module.css';
import {clsx} from 'clsx';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const SEEK_EPSILON_SECONDS = 0.05;

interface VideoSeekPreviewProps {
	src: string;
	poster?: string;
	time: number;
	className?: string;
}

/**
 * The frame at `time`, from a second muted copy of the clip that only seeks. It stays hidden until a
 * frame has been painted, so a phone that will not load it shows the time alone, never a black box.
 */
export function VideoSeekPreview({src, poster, time, className}: VideoSeekPreviewProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [hasFrame, setHasFrame] = useState(false);
	const seekTo = useCallback((targetTime: number) => {
		const video = videoRef.current;
		if (!video || video.readyState < 1) return;
		const nextTime = clampMediaTime(targetTime, video.duration);
		if (Math.abs(video.currentTime - nextTime) <= SEEK_EPSILON_SECONDS) return;
		video.currentTime = nextTime;
	}, []);
	useLayoutEffect(() => {
		const video = videoRef.current;
		return () => detachMediaElementSource(video);
	}, []);
	useEffect(() => {
		seekTo(time);
	}, [seekTo, time]);
	const handleLoadedMetadata = useCallback(() => seekTo(time), [seekTo, time]);
	const handleFrame = useCallback(() => setHasFrame(true), []);
	return (
		<video
			ref={videoRef}
			className={clsx(styles.seekPreviewVideo, !hasFrame && styles.seekPreviewPending, className)}
			src={src}
			poster={poster}
			preload="auto"
			muted
			playsInline
			disablePictureInPicture
			onLoadedMetadata={handleLoadedMetadata}
			onSeeked={handleFrame}
			onLoadedData={handleFrame}
			data-has-frame={hasFrame}
			data-flx="voice.media-player.video-seek-preview"
		>
			<track kind="captions" data-flx="voice.media-player.video-seek-preview.track" />
		</video>
	);
}
